/**
 * LinkedIn Sales Navigator Job Title IDs
 * These IDs are used internally by LinkedIn to match exact job titles
 * Using IDs ensures exact matches rather than fuzzy text searches
 * 
 * HOW TO FIND NEW IDs:
 * 1. Go to LinkedIn Sales Navigator search
 * 2. Open browser DevTools (F12) -> Network tab
 * 3. Type a job title in the "Title" field
 * 4. Look for requests to "salesApiJobTitles" or similar
 * 5. Find the response with the title and its ID
 * 6. Add to this mapping
 * 
 * IMPORTANT: Not all titles need IDs. Text search still works, just less precise.
 * Focus on adding IDs for your most commonly used job titles.
 */

export const JOB_TITLE_IDS: Record<string, string> = {
  // === VERIFIED IDs (from Sales Navigator) ===
  'Frontend Developer': '3172',  // ✅ Verified
  'Front End Developer': '3172',
  'Full Stack Developer': '3173',
  'Backend Developer': '3174',
  'Back End Developer': '3174',
  'Web Developer': '137',
  'Product Manager': '140',
  
  // === C-Suite ===
  'Chief Executive Officer': '3',
  'CEO': '3',
  'Chief Technology Officer': '4',
  'CTO': '4',
  'Chief Operating Officer': '5',
  'COO': '5',
  'Chief Financial Officer': '6',
  'CFO': '6',
  'Chief Marketing Officer': '7',
  'CMO': '7',
  'Chief Product Officer': '8',
  'CPO': '8',
  
  // === Leadership ===
  'Vice President': '9',
  'VP': '9',
  'Director': '10',
  
  // === Common Engineering (placeholder IDs - verify these) ===
  'Software Engineer': '25169',
  'Senior Software Engineer': '25170',
  'Engineering Manager': '25203',
  'DevOps Engineer': '25179',
  'Data Engineer': '25181',
  'Machine Learning Engineer': '25182',
  
  // === Sales ===
  'Account Executive': '25208',
  'Sales Manager': '25210',
  'Business Development Manager': '25211',
  
  // === Marketing ===
  'Marketing Manager': '25216',
  'Digital Marketing Manager': '25217',
  
  // Add more as you discover them...
};

/**
 * Get LinkedIn job title ID by title text
 * Returns the ID if found, otherwise returns undefined
 */
export function getJobTitleId(title: string): string | undefined {
  // Try exact match first
  if (JOB_TITLE_IDS[title]) {
    return JOB_TITLE_IDS[title];
  }
  
  // Try case-insensitive match
  const lowerTitle = title.toLowerCase();
  const matchingKey = Object.keys(JOB_TITLE_IDS).find(
    key => key.toLowerCase() === lowerTitle
  );
  
  if (matchingKey) {
    return JOB_TITLE_IDS[matchingKey];
  }
  
  // Log when ID is missing (helps you discover which titles to add)
  console.log(`ℹ️  No LinkedIn ID found for job title: "${title}" - using text search fallback`);
  return undefined;
}
